import { useCallback } from 'react'
import { postRequestCustomerPortalSession } from '../../../assets/requests'

/**
 * Hook for managing customer portal and billing operations
 * Handles customer portal session creation and navigation
 */
const useCustomerPortal = () => {
  /**
   * Handles customer portal form submission
   * Creates a customer portal session and redirects user to Stripe billing portal
   * @param {Event} e - Form submission event
   */
  const handleCustomerPortalSubmit = useCallback(async e => {
    e.preventDefault()

    try {
      // Extract stripe customer ID from form
      const stripeId = e.target.childNodes[0].innerHTML
      console.log('Customer portal request for stripe ID:', stripeId)

      if (!stripeId) {
        console.error('No stripe customer ID found')
        return
      }

      const config = {
        payload: {
          DOMAIN: process.env.REACT_APP_FRONTEND_URL,
          CUSTOMER_ID: stripeId
        }
      }

      const response = await postRequestCustomerPortalSession(config)

      if (response.status === 301 && response.url) {
        // Redirect to customer portal
        window.location = response.url
      } else {
        console.error(
          'Unexpected response from customer portal session:',
          response
        )
      }

      console.log('Customer portal response:', response)
    } catch (error) {
      console.error('Error creating customer portal session:', error)
    }
  }, [])

  /**
   * Creates a customer portal session for a given stripe customer ID
   * @param {string} stripeCustomerId - The Stripe customer ID
   * @returns {Promise<Object>} Response from customer portal session creation
   */
  const createCustomerPortalSession = useCallback(async stripeCustomerId => {
    try {
      if (!stripeCustomerId) {
        throw new Error('Stripe customer ID is required')
      }

      const config = {
        payload: {
          DOMAIN: process.env.REACT_APP_FRONTEND_URL,
          CUSTOMER_ID: stripeCustomerId
        }
      }

      const response = await postRequestCustomerPortalSession(config)
      return response
    } catch (error) {
      console.error('Error creating customer portal session:', error)
      throw error
    }
  }, [])

  /**
   * Navigates to customer portal for billing management
   * @param {string} stripeCustomerId - The Stripe customer ID
   * @returns {Promise<boolean>} True if navigation was successful
   */
  const navigateToCustomerPortal = useCallback(
    async stripeCustomerId => {
      try {
        const response = await createCustomerPortalSession(stripeCustomerId)

        if (response.status === 301 && response.url) {
          window.location = response.url
          return true
        } else {
          console.error('Failed to get portal URL from response:', response)
          return false
        }
      } catch (error) {
        console.error('Error navigating to customer portal:', error)
        return false
      }
    },
    [createCustomerPortalSession]
  )

  /**
   * Gets the frontend domain URL for customer portal redirects
   * @returns {string} Frontend URL from environment variables
   */
  const getFrontendDomain = useCallback(() => {
    return process.env.REACT_APP_FRONTEND_URL
  }, [])

  return {
    // Actions
    handleCustomerPortalSubmit,
    createCustomerPortalSession,
    navigateToCustomerPortal,

    // Utilities
    getFrontendDomain
  }
}

export default useCustomerPortal
